import React, { useState } from "react";
import DSBox from "../../../system-design/DSBox";
import DSButton from "../../../system-design/DSButton";
import DSCard from "../../../system-design/DSCard";
import DSCardContent from "../../../system-design/DSCardContent";
import DSChip from "../../../system-design/DSChip";
import DSSectionHeading from "../../../system-design/DSSectionHeading";
import DSTypography from "../../../system-design/DSTypography";
import { sectionCardSx } from "../../../system-design/sectionCardSx";
import { ClockConfigValue } from "../../../../api/clockConfig";
import ClockConfigOverrideDialog from "../../../ClockConfiguration/components/ClockConfigOverrideDialog";
import {
  useClockConfigOverrides,
  useClockGlobalConfig,
} from "../../../ClockConfiguration/hooks/useClockConfig";
import { useDevice } from "../../context/DeviceModalContext";

/**
 * What this clock is actually running, and which of those values are its own.
 *
 * The two version numbers come from the clock's heartbeat, so they say what it has
 * APPLIED — not what we last saved. They lag a save by up to a day (the daily 04:xx
 * fetch) unless the Refresh Config command is sent, which is exactly the gap worth
 * seeing here.
 */
const DeviceConfigOverridesCard: React.FC = () => {
  const { device } = useDevice();
  const [editing, setEditing] = useState(false);

  const { data: globalConfig } = useClockGlobalConfig();
  const { data: overrides } = useClockConfigOverrides();

  const override = overrides?.find((entry) => entry.id === device?.id);
  const overrideValues = override?.values ?? {};
  const globalValues = globalConfig?.values ?? {};

  // Every key either document sets. A key set nowhere is simply not listed: its value
  // is a default compiled into the clock's build, which this screen cannot know.
  const keys = Array.from(
    new Set([...Object.keys(globalValues), ...Object.keys(overrideValues)]),
  ).sort();

  const appliedVersion = device?.configVersion;
  const appliedPersonalVersion = device?.configPersonalVersion;

  // A clock that has never reported a version is on a build without the config
  // mechanism, or has not sent a heartbeat since it shipped.
  const hasReported = appliedVersion !== undefined;
  const globalStale =
    hasReported &&
    globalConfig !== null &&
    globalConfig !== undefined &&
    appliedVersion !== globalConfig.version;
  const personalStale =
    hasReported && (appliedPersonalVersion ?? 0) !== (override?.version ?? 0);

  return (
    <DSCard className="card-stats" sx={[sectionCardSx, { mb: 2 }]}>
      <DSSectionHeading>Clock Configuration</DSSectionHeading>
      <DSCardContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <DSBox sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <DSChip
              size="small"
              label={`applied global v${hasReported ? appliedVersion : "—"}${
                globalConfig ? ` / saved v${globalConfig.version}` : ""
              }`}
              color={globalStale ? "warning" : "default"}
            />
            <DSChip
              size="small"
              label={`applied override v${
                hasReported ? (appliedPersonalVersion ?? 0) : "—"
              }${override ? ` / saved v${override.version}` : ""}`}
              color={personalStale ? "warning" : "default"}
            />
          </DSBox>

          {!hasReported && (
            <DSTypography variant="caption" color="text.secondary">
              This clock has not reported a config version yet — it may be on a
              build from before the config mechanism.
            </DSTypography>
          )}

          {(globalStale || personalStale) && (
            <DSTypography variant="caption" color="text.secondary">
              The clock has not picked the latest save up yet. It will at its
              daily 04:xx fetch, or immediately with the &quot;Refresh
              Config&quot; command.
            </DSTypography>
          )}

          <DSBox
            sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}
          >
            {keys.length === 0 && (
              <DSTypography variant="caption" color="text.secondary">
                Nothing is configured — this clock runs entirely on the defaults
                compiled into its build.
              </DSTypography>
            )}

            {keys.map((key) => {
              const isOverridden = overrideValues[key] !== undefined;
              const effective: ClockConfigValue =
                overrideValues[key] ?? globalValues[key];

              return (
                <DSBox
                  key={key}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <DSTypography
                    variant="body2"
                    sx={{ minWidth: 200 }}
                    fontWeight={isOverridden ? 600 : 400}
                  >
                    {key}
                  </DSTypography>
                  <DSTypography variant="body2">
                    {String(effective)}
                  </DSTypography>
                  <DSChip
                    size="small"
                    label={isOverridden ? "override" : "global"}
                    color={isOverridden ? "primary" : "default"}
                  />
                </DSBox>
              );
            })}
          </DSBox>

          <DSBox sx={{ mt: 1 }}>
            <DSButton
              size="small"
              variant="outlined"
              onClick={() => setEditing(true)}
            >
              {override ? "Edit overrides" : "Add overrides"}
            </DSButton>
          </DSBox>
        </DSBox>
      </DSCardContent>

      {device?.id && (
        <ClockConfigOverrideDialog
          open={editing}
          onClose={() => setEditing(false)}
          clockId={device.id}
          values={overrideValues}
        />
      )}
    </DSCard>
  );
};

export default DeviceConfigOverridesCard;
